import { queryOptions, useQuery } from "@tanstack/react-query"
import { queryClient } from "@/api/constants"
import { PNApiOperations } from "@/api/operations"
import type { GetAppConfigBody } from "@/api/dto/users"
import { getElectronAppConfig, isElectronApp } from "@/utils/electron"
import { ENGLISH_ONLY_APP_CONFIG, parseAppConfig } from "@/utils/localization-access"

export const APP_CONFIG_QUERY_KEY = ["app-config", isElectronApp ? "electron" : "web"] as const
export const APP_CONFIG_LAUNCH_TIMEOUT_MS = 3000

const fetchRuntimeAppConfig = async (signal?: AbortSignal): Promise<GetAppConfigBody> => {
	if (isElectronApp) {
		const electronConfig = await getElectronAppConfig()
		if (electronConfig === undefined) {
			return ENGLISH_ONLY_APP_CONFIG
		}
		return parseAppConfig(electronConfig)
	}

	return parseAppConfig(await PNApiOperations.getAppConfig(signal))
}

const fetchRuntimeAppConfigWithinLaunchDeadline = async (
	querySignal?: AbortSignal,
): Promise<GetAppConfigBody> => {
	const controller = new AbortController()
	const abortFromQuery = () => controller.abort(querySignal?.reason)
	if (querySignal?.aborted) abortFromQuery()
	else querySignal?.addEventListener("abort", abortFromQuery, { once: true })

	let timeout: ReturnType<typeof setTimeout> | undefined
	const deadline = new Promise<never>((_, reject) => {
		timeout = setTimeout(() => {
			controller.abort()
			reject(
				new Error(`App Config launch request exceeded ${APP_CONFIG_LAUNCH_TIMEOUT_MS}ms.`),
			)
		}, APP_CONFIG_LAUNCH_TIMEOUT_MS)
	})

	try {
		return await Promise.race([fetchRuntimeAppConfig(controller.signal), deadline])
	} finally {
		if (timeout) clearTimeout(timeout)
		querySignal?.removeEventListener("abort", abortFromQuery)
	}
}

export const appConfigQueryOptions = queryOptions({
	queryKey: APP_CONFIG_QUERY_KEY,
	queryFn: ({ signal }) => fetchRuntimeAppConfigWithinLaunchDeadline(signal),
	staleTime: Infinity,
	gcTime: Infinity,
	// App startup has one total three-second budget; retries must not extend it.
	retry: false,
})

export const useAppConfigQuery = () => useQuery(appConfigQueryOptions)

/**
 * Resolve App Config before i18n initializes, then seed the page-lifetime query cache.
 * A launch that cannot retrieve valid configuration remains English-only.
 */
export const loadAppConfigForLaunch = async (): Promise<GetAppConfigBody> => {
	try {
		return await queryClient.fetchQuery(appConfigQueryOptions)
	} catch (error) {
		console.warn("Failed to load App Config; using English for this launch.", error)
		queryClient.setQueryData(APP_CONFIG_QUERY_KEY, ENGLISH_ONLY_APP_CONFIG)
		return ENGLISH_ONLY_APP_CONFIG
	}
}
